import { useAuth0 } from '@auth0/auth0-react';
import { Button, FullPageLoader, FullPageStatus } from 'corso-admin-ui';
import { PropsWithChildren } from 'react';
import { setAccessTokenGetter } from '~/accessToken';
import env from '~/env';
import { addBreadcrumb } from '~/utils/observability';
import { AuthenticatedUserProvider } from './AuthenticatedUserProvider';

export default function Auth0UserIntoAuthProvider({
  children,
}: PropsWithChildren) {
  const {
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    user: auth0User,
  } = useAuth0();

  // registered before any API call so requests carry a verifiable Auth0 access
  // token; the audience is requested here (not on the provider alone) so it is
  // part of the token grant
  setAccessTokenGetter(() =>
    getAccessTokenSilently({
      authorizationParams: { audience: env.VITE_AUTH0_AUDIENCE },
    }),
  );

  if (isLoading) return <FullPageLoader />; // authenticating with auth0

  if (error) {
    if (
      error.message === 'Invalid state' ||
      error.message === 'Invalid authorization code'
    ) {
      addBreadcrumb({
        message: `Handled Auth0 Error: ${error.message}`,
        level: 'warning',
      });
      // the stale code/state is in the query string, so log in again without it
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      }).catch((loginWithRedirectError) => {
        console.error(
          new Error('Error With Auth0 Login Redirect', {
            cause: loginWithRedirectError,
          }),
        );
      });
    } else {
      console.error(new Error('Unhandled Auth0 Error', { cause: error }));
    }
    // ? possibly use PageStatus.Error
    return (
      <FullPageStatus
        title="Session Expired"
        details="Please log in again to continue."
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <Button variant="primary" size="lg" onClick={() => logout()}>
            Continue
          </Button>
        </div>
      </FullPageStatus>
    );
  }

  if (!isAuthenticated || !auth0User) {
    // ? if embedded, use popup login, since auth0 won't allow embedding
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    }).catch((loginWithRedirectError) => {
      console.error('Error With Auth0 Login Redirect', loginWithRedirectError);
    });
    return <FullPageLoader />;
  }

  if (!auth0User.sub) {
    throw new Error('Not Auth0 Subject');
  }

  return (
    <AuthenticatedUserProvider auth0sub={auth0User.sub}>
      {children}
    </AuthenticatedUserProvider>
  );
}
